import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Grid, styled, Typography } from "@mui/material";
import { useForm } from "react-hook-form";

import { BlFormCheckbox } from "Forms/Components/BlFormCheckbox";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import {
  type UploadMediaFileFormData,
  uploadMediaFileFormSchema,
} from "MusicAudio/Forms/uploadMediaFileFormSchema";
import { BlModal } from "Shared/Dialogs/BlModal";

const HiddenInput = styled("input")({
  display: "none",
});

interface UploadMediaFileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: UploadMediaFileFormData) => void | Promise<void>;
}

export const UploadMediaFileModal = ({
  isOpen,
  onClose,
  onConfirm,
}: UploadMediaFileModalProps): React.ReactNode => {
  const { t } = useResource();

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<UploadMediaFileFormData>({
    resolver: zodResolver(uploadMediaFileFormSchema),
    defaultValues: {
      displayName: "",
      file: undefined,
      licenseConfirmed: false,
    },
  });

  const selectedFile = watch("file");

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setValue("file", file, { shouldValidate: true });
      if (!watch("displayName")) {
        setValue("displayName", file.name.replace(/\.[^/.]+$/, ""));
      }
    }
  };

  const onSubmit = async (data: UploadMediaFileFormData) => {
    await onConfirm(data);
    reset();
    onClose();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <BlModal
      isOpen={isOpen}
      title={t(Resources.MusicAudio.UploadModal.Title)}
      onCancel={handleClose}
      onConfirm={handleSubmit(onSubmit)}
      cancelButtonText={t(Resources.Common.Cancel)}
      confirmButtonText={t(Resources.MusicAudio.UploadModal.Upload)}
      maxWidth="500px"
    >
      <Grid container spacing={Spacing.M}>
        <Grid size={12}>
          <BlFormInput
            control={control}
            name="displayName"
            label={t(Resources.MusicAudio.UploadModal.DisplayName)}
            errors={errors}
            placeholder={t(
              Resources.MusicAudio.UploadModal.DisplayNamePlaceholder,
            )}
          />
        </Grid>
        <Grid size={12}>
          <Typography
            variant="body2"
            sx={{ mb: 1, color: (theme) => theme.palette.common.white }}
          >
            {t(Resources.MusicAudio.UploadModal.SelectFile)}
          </Typography>
          <label htmlFor="media-file-upload">
            <HiddenInput
              id="media-file-upload"
              type="file"
              accept="audio/*"
              onChange={handleFileChange}
            />
            <Button variant="outlined" component="span" fullWidth>
              {selectedFile
                ? (selectedFile as File).name
                : t(Resources.MusicAudio.UploadModal.SelectFile)}
            </Button>
          </label>
          {errors.file && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
              {errors.file.message}
            </Typography>
          )}
        </Grid>
        <Grid size={12}>
          <BlFormCheckbox
            control={control}
            name="licenseConfirmed"
            errors={errors}
            label={t(Resources.MusicAudio.UploadModal.LicenseConfirmation)}
          />
        </Grid>
      </Grid>
    </BlModal>
  );
};
